import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import {
    breakGlassOffered,
    breakGlassReasonValid,
    buildPublishBody,
    describeBlockers,
    describePublishError,
    fieldLabel,
    publishIsBlocked,
} from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

const aiChip = (ai) => {
    if (!ai) return null;
    return (
        <Space wrap>
            <Tag color={ai.state === 'APPROVED' ? 'green' : 'orange'}>{ai.state === 'APPROVED' ? 'AI drafted, approved' : 'AI drafted - not approved'}</Tag>
            {ai.editedByHuman && <Tag color="blue">Edited by a human</Tag>}
        </Space>
    );
};

/** Every blocker the API sent, in words, with the questions it names. The UI never computes blockers. */
const BlockerList = ({ blockers }) => (
    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        {blockers.map((b) => (
            <li key={b.code} data-testid={`blocker-${b.code}`}>
                {b.text}{b.questionNos.length > 0 ? ` Affects: ${b.questionNos.join(', ')}.` : ''}
            </li>
        ))}
    </ul>
);

const PublishDetail = ({ preview }) => (
    <div>
        {preview.added.length > 0 && (
            <div data-testid="publish-added">
                <Text strong>New questions ({preview.added.length})</Text>
                <ul style={{ paddingLeft: 18 }}>
                    {preview.added.map((a) => (
                        <li key={a.draftId}><Text strong>{a.questionNo}</Text> {a.question?.question} {aiChip(a.aiDraft)}</li>
                    ))}
                </ul>
            </div>
        )}
        {preview.revised.length > 0 && (
            <div data-testid="publish-revised">
                <Text strong>Revised questions ({preview.revised.length})</Text>
                <ul style={{ paddingLeft: 18 }}>
                    {preview.revised.map((r) => (
                        <li key={r.draftId}>
                            <Text strong>{r.questionNo}</Text> {aiChip(r.aiDraft)}
                            <ul>
                                {(r.changedFields || []).map((c) => (
                                    <li key={c.field}>{fieldLabel(c.field)}: "{String(c.published ?? '-')}" becomes "{String(c.draft ?? '-')}"</li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>
        )}
        {preview.retired.length > 0 && (
            <div data-testid="publish-retired">
                <Text strong>Retired questions ({preview.retired.length})</Text>
                <ul style={{ paddingLeft: 18 }}>
                    {preview.retired.map((r) => (
                        <li key={r.tombstoneId}><Text strong>{r.questionNo}</Text> {r.published?.question}</li>
                    ))}
                </ul>
            </div>
        )}
    </div>
);

/**
 * Publish the standard's pending next version. The summary and blockers come from the API
 * (publish-preview) - the UI never computes them. Publishing stays disabled while any AI draft is
 * unapproved (Commandment IV); the API enforces the same rule inside the publish transaction.
 * Published versions are immutable (Commandment VII); projects already pinned keep their version.
 */
const PublishPanel = ({ standardId, standardLabel, preview, onPublished }) => {
    const { request } = useApiRequest();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [lastPublish, setLastPublish] = useState(null);
    const [breakGlass, setBreakGlass] = useState(false);
    const [breakGlassReason, setBreakGlassReason] = useState('');

    const publish = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('POST', `/admin/question-bank/versions/${standardId}/publish`, {
                body: buildPublishBody({ breakGlass, breakGlassReason }),
            });
            setLastPublish(data);
            setOpen(false);
            setBreakGlass(false);
            setBreakGlassReason('');
            message.success(data?.message || 'Version published.');
            await onPublished();
        } catch (err) {
            const view = describePublishError(err);
            setError(view);
            if (view.reload) await onPublished();
        } finally {
            setBusy(false);
        }
    };

    if (preview.status === 'loading' && !preview.data) return <Spin />;
    if (preview.status === 'error') {
        return <Alert type="error" message={userMessageForError(preview.error)} action={<Button size="small" onClick={preview.reload}>Retry</Button>} />;
    }
    const p = preview.data;
    if (!p) return null;
    const blocked = publishIsBlocked(p, { breakGlass, breakGlassReason });
    const blockers = describeBlockers(p);
    const offerBreakGlass = breakGlassOffered(p);
    const total = p.summary.added + p.summary.revised + p.summary.retired;

    return (
        <div>
            {lastPublish && (
                <Alert
                    type="success"
                    closable
                    style={{ marginBottom: 12 }}
                    data-testid="publish-result"
                    message={lastPublish.message || 'Version published.'}
                    description={(lastPublish.aiDraftsPublished || []).length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {lastPublish.aiDraftsPublished.map((d) => (
                                <li key={d.draftId}>
                                    {d.questionNo}: AI draft approved by {d.approvedBy} on {formatDdMmYyyy(d.approvedAt)}{d.editedByHuman ? ', edited by a human' : ''}
                                </li>
                            ))}
                        </ul>
                    ) : 'Projects already pinned to the previous version keep it; projects that pin from now on get this one.'}
                />
            )}

            {!p.draftVersion ? (
                <Empty description={`Nothing is waiting to be published for ${standardLabel}. Draft, revise or retire a question first.`} />
            ) : (
                <div>
                    <p style={{ marginTop: 0 }}>
                        Pending next version <Text strong>{p.draftVersion.label}</Text>
                        {p.publishedVersion ? <span> (current published version {p.publishedVersion.label})</span> : <span> (first version for this standard)</span>}
                    </p>
                    <Space wrap style={{ marginBottom: 12 }} data-testid="publish-summary">
                        <Tag>{p.summary.added} added</Tag>
                        <Tag>{p.summary.revised} revised</Tag>
                        <Tag>{p.summary.retired} retired</Tag>
                        <Tag>{p.summary.aiDrafts} AI drafts ({p.summary.aiApproved} approved, {p.summary.aiUnapproved} unapproved, {p.summary.aiEditedByHuman} edited by a human)</Tag>
                    </Space>
                    {blockers.length > 0 && (
                        <Alert
                            type="error"
                            style={{ marginBottom: 12 }}
                            data-testid="publish-blockers"
                            message={p.summary.aiUnapproved > 0 ? `${p.summary.aiUnapproved} AI draft(s) are not approved yet` : 'Publishing is blocked'}
                            description={<BlockerList blockers={blockers} />}
                        />
                    )}
                    {total === 0 && <Alert type="info" style={{ marginBottom: 12 }} message="There are no changes to publish." />}
                    <Button onClick={() => { setError(null); setOpen(true); }} disabled={total === 0 && p.blockers.length === 0}>Review and publish</Button>
                </div>
            )}

            <Modal
                open={open}
                title={`Publish ${standardLabel} ${p.draftVersion?.label || ''}`}
                okText="Publish version"
                onOk={publish}
                onCancel={() => setOpen(false)}
                confirmLoading={busy}
                okButtonProps={{ disabled: blocked }}
            >
                {p.draftVersion && (
                    <div>
                        <Alert
                            type="warning"
                            style={{ marginBottom: 12 }}
                            message="Publishing is permanent."
                            description="The published version cannot be edited afterwards; later changes create the next version. Projects already pinned to the previous version keep it unchanged. Only projects that pin after this publish see the new version."
                        />
                        <Space wrap style={{ marginBottom: 12 }} data-testid="publish-modal-summary">
                            <Tag>{p.summary.added} added</Tag>
                            <Tag>{p.summary.revised} revised</Tag>
                            <Tag>{p.summary.retired} retired</Tag>
                            <Tag>{p.summary.aiApproved} AI drafts approved</Tag>
                            <Tag>{p.summary.aiUnapproved} AI drafts unapproved</Tag>
                        </Space>
                        {blocked && (
                            <Alert
                                type="error"
                                style={{ marginBottom: 12 }}
                                data-testid="publish-modal-blocked"
                                message="Publishing is blocked"
                                description={blockers.length > 0 ? <BlockerList blockers={blockers} /> : 'There is nothing to publish.'}
                            />
                        )}
                        {offerBreakGlass && (
                            <div style={{ marginBottom: 12 }} data-testid="break-glass-publish">
                                <Checkbox checked={breakGlass} onChange={(e) => setBreakGlass(e.target.checked)}>
                                    Publish under break-glass. Allowed only because you are the only platform admin; it is recorded in the audit log.
                                </Checkbox>
                                {breakGlass && (
                                    <Input.TextArea
                                        aria-label="Break-glass reason"
                                        rows={2}
                                        placeholder="Why can a second admin not publish? (at least 15 characters)"
                                        value={breakGlassReason}
                                        onChange={(e) => setBreakGlassReason(e.target.value)}
                                    />
                                )}
                                {breakGlass && !breakGlassReasonValid(breakGlassReason) && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>Publish stays disabled until the reason is at least 15 characters.</Text>
                                )}
                            </div>
                        )}
                        <PublishDetail preview={p} />
                        {error && (
                            <Alert
                                type="error"
                                style={{ marginTop: 12 }}
                                data-testid="publish-error"
                                message={error.message}
                                description={error.questionNos.length > 0
                                    ? `${error.kind === 'content-changed' ? 'Changed after approval' : 'Still unapproved'}: ${error.questionNos.join(', ')}.`
                                    : undefined}
                            />
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PublishPanel;
